import { Workbook } from "exceljs";
import * as ExcelJS from "exceljs";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../recoil/pub.atom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { ExpenseMainType, ExpenseSubType } from "../../../api/settlement";
import {
  getExpenseTypeText,
  getPayTypeText,
} from "../../solution/utils/settlement.util";
import { Const } from "../../../common/const/const";
import { formatDateKor } from "../../../utils/date";
import {
  SettlementMonthResponse,
  SettlementMonthExcelResponse,
  SettlementExpenseTypeAndExpensesDto,
  SettlementMonthNetIncomeResponse,
  getSettlementMonthExcel,
  SettlementMonthExcelClosingResponse,
} from "../../../api/settlement-month";
ChartJS.register(ArcElement, Tooltip, Legend);

// 숫자 -> 엑셀 영문 변환
export const numberToExcelColumn = (num: number): string => {
  let result = "";
  let n = num;

  while (n > 0) {
    n--; // 0-based 보정
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }

  return result;
};

export const useMonthExcel = () => {
  const pub = useRecoilValue(pubState);

  // 하단 도트 테두리 적용 함수
  const applyBottomDottedBorder = (
    sheet: ExcelJS.Worksheet,
    startRow: number,
    endRow: number,
  ) => {
    for (let row = startRow; row <= endRow; row++) {
      for (let col = 2; col <= 5; col++) {
        // B(2) ~ E(5)
        const cell = sheet.getCell(row, col);

        cell.border = {
          bottom: { style: "dotted", color: { argb: "e7e6e6" } },
        };
      }
    }
  };

  const expenseSheetStyle = (sheet: ExcelJS.Worksheet) => {
    // 셀 스타일 설정 =========================================================================
    for (let col = 1; col <= 15; col++) {
      const column = sheet.getColumn(col);

      // 열 너비 설정
      if (col === 1 || col === 5 || col === 9 || col === 13)
        column.width = 11; // A, E, I, M
      else if (col === 2 || col === 6 || col === 10 || col === 14)
        column.width = 28; // B, F, J, N
      else if (col === 4 || col === 8 || col === 12)
        column.width = 1; // D, H, L
      else column.width = 20;

      // 열 전체  정렬
      column.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        // 숫자 포맷
        if (typeof cell.value === "number" || typeof cell.value === "object") {
          cell.numFmt = "₩ #,##0";
          // 우측 정렬
          cell.alignment = {
            vertical: "middle",
            horizontal: "right",
          };
        }

        cell.font = {
          ...cell.font,
          name: "돋움",
        };
      });
    }

    const leftColumn = ["B", "R", "J", "N"];

    for (let row = 1; row <= 58; row++) {
      const column = sheet.getRow(row);

      // 셀 높이 설정
      if (row === 2 || row === 31) column.height = 32;
      else if (row === 3 || row === 32) column.height = 29;
      else if (row === 29 || row === 58) column.height = 33;

      if (
        row !== 2 &&
        row !== 3 &&
        row !== 29 &&
        row !== 31 &&
        row !== 32 &&
        row !== 58
      ) {
        leftColumn.forEach((column) => {
          sheet.getCell(`${column}${row}`).alignment = {
            vertical: "middle",
            horizontal: "left",
          };
        });
      }

      // 열 전체 border 지정
      column.border = {
        top: { style: "dotted" },
        left: { style: "dotted" },
        bottom: { style: "dotted" },
        right: { style: "dotted" },
      };
    }
  };

  /////////////////////////////////////
  //            결산서 생성           //
  ////////////////////////////////////
  const createClosingSheet = async (
    workbook: Workbook,
    data: SettlementMonthExcelClosingResponse,
    month: number,
  ) => {
    const sheet = workbook.addWorksheet(`${month}월 ${getPubName()} 결산서`);
    sheet.views = [{ showGridLines: false }]; // 기본 선 제거

    // 제목
    sheet.mergeCells("B1 : H2");
    sheet.getCell("B1").value = `${month}월 ${getPubName()} 월간 결산서`;

    // 손익 ===============================================================================
    sheet.getCell("B3").value = "손익";
    sheet.getCell("B3").font = { size: 20, bold: true };
    sheet.mergeCells("B4 : C5");
    sheet.getCell("B4").font = { size: 22, bold: true };
    sheet.getCell("B4").value = {
      formula: `=SUM(D4-D7)`,
      result: data.totalRevenue - data.totalExpense,
    };

    // 손익률 ==============================================================================
    sheet.getCell("C6").value = "손익률";
    sheet.getCell("C6").font = {
      size: 20,
      bold: true,
      color: { argb: "47d35a" },
    };
    sheet.mergeCells("C7 : C8");
    sheet.getCell("C7").value = {
      formula: `=IFERROR(B4/D4,0)`,
      result: (
        (data.totalRevenue - data.totalExpense) /
        data.totalRevenue
      ).toFixed(2),
    };
    sheet.getCell("C7").font = {
      size: 20,
      bold: true,
      color: { argb: "47d35a" },
    };

    // 수입 ===============================================================================
    sheet.getCell("D3").value = "수입";
    sheet.getCell("D3").font = {
      size: 16,
      bold: true,
      color: { argb: "619783" },
    };
    sheet.mergeCells("D4 : E5");
    sheet.getCell("D4").font = {
      size: 16,
      bold: true,
      color: { argb: "619783" },
    };
    sheet.getCell("D4").value = {
      formula: `=SUM(E18)`,
      result: data.totalRevenue,
    };

    // 지출 ===============================================================================
    sheet.getCell("D6").value = "지출";
    sheet.getCell("D6").font = {
      size: 16,
      bold: true,
      color: { argb: "df6d60" },
    };
    sheet.mergeCells("D7 : E8");
    sheet.getCell("D7").font = {
      size: 16,
      bold: true,
      color: { argb: "df6d60" },
    };
    sheet.getCell("D7").value = {
      formula: `=SUM(E34)`,
      result: data.totalExpense,
    };

    // 비고 =============================================================================
    sheet.getCell("G3").value = `비고`;
    sheet.mergeCells("G3 : H3");
    sheet.getCell("G3").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "e8e8e8" },
    };
    sheet.getCell("G4").value = `본사 시재`;
    sheet.getCell("H4").value = `${data.runnerSeed?.toLocaleString()} 래빗`;
    sheet.getCell("G5").value = `${month}월 래빗 매출`;
    sheet.getCell("H5").value = `${(
      data.revenueSeedTotal / Const.SEED_RATE
    )?.toLocaleString()} 래빗`;
    sheet.getCell("G6").value = `${month}월 래빗 지출`;
    sheet.getCell("H6").value = `${(
      data.expenseSeedTotal / Const.SEED_RATE
    )?.toLocaleString()} 래빗`;

    // 시작 시재 =============================================================================
    sheet.getCell(`G8`).value = `${month}월 시작 시재`;
    sheet.mergeCells(`G8 : H8`);
    sheet.getCell(`G8`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "fefe00" },
    };
    sheet.getCell(`G9`).value = "현금";
    sheet.getCell(`H9`).value = data.startBalance.cash;
    sheet.getCell(`G10`).value = "계좌";
    sheet.getCell(`H10`).value = data.startBalance.bank;
    sheet.getCell(`G11`).value = "총계";
    sheet.getCell(`H11`).value = {
      formula: `=SUM(H9+H10)`,
      result: data.startBalance.cash + data.startBalance.bank,
    };

    // 마감 시재 =============================================================================
    sheet.getCell(`G12`).value = `${month}월 마감 시재`;
    sheet.mergeCells(`G12 : H12`);
    sheet.getCell(`G12`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "8ed973" },
    };
    sheet.getCell(`G13`).value = "현금";
    sheet.getCell(`H13`).value = data.endBalance.cash;
    sheet.getCell(`G14`).value = "계좌";
    sheet.getCell(`H14`).value = data.endBalance.bank;
    sheet.getCell(`G15`).value = "정산 합계"; // 시작시재 + 손익
    sheet.getCell(`H15`).value = {
      formula: `=SUM(H11 + B4)`,
      result:
        data.startBalance.cash +
        data.startBalance.bank +
        data.totalRevenue -
        data.totalExpense,
    };
    sheet.getCell(`G16`).value = "수수료 오차";
    sheet.getCell(`H16`).value = data.cardRevenue - data.cardIncome;
    sheet.getCell(`G17`).value = "실잔고총합";
    sheet.getCell(`H17`).value = {
      formula: `=SUM(H13+H14+H16)`,
      result:
        data.endBalance.cash +
        data.endBalance.bank +
        data.cardRevenue -
        data.cardIncome,
    };

    // 수입 내역 =============================================================================
    sheet.getCell("B10").value = "01. 수 입 내 역";
    sheet.mergeCells("B10 : C10");
    sheet.getCell("B10").font = { size: 12, bold: true };

    sheet.getCell("B11").value = "구분";
    sheet.getCell("B11").font = { size: 10 };
    sheet.getCell("C11").value = "적요";
    sheet.getCell("C11").font = { size: 10 };
    sheet.mergeCells("C11 : D11");
    sheet.getCell("E11").value = "금액";
    sheet.getCell("E11").font = { size: 10 };
    data.revenues.forEach((r, i) => {
      sheet.getCell(`B${12 + i}`).value = getPayTypeText(r.paymentMethod);
      sheet.getCell(`E${12 + i}`).value = r.amount;
      sheet.getCell(`B${12 + i}`).font = { size: 10 };
    });
    sheet.getCell("B18").value = "소계";
    sheet.getCell("E18").value = {
      formula: `=SUM(E12:E17)`,
      result: data.revenues.reduce((sum, curr) => (sum += curr.amount), 0),
    };

    // 지출 내역 =============================================================================
    sheet.getCell("B20").value = "02. 지 출 내 역";
    sheet.mergeCells("B20 : C20");
    sheet.getCell("B20").font = { size: 12, bold: true };

    sheet.getCell("B21").value = "구분";
    sheet.getCell("B21").font = { size: 10 };
    sheet.getCell("C21").value = "적요";
    sheet.getCell("C21").font = { size: 10 };
    sheet.mergeCells("C21 : D21");
    sheet.getCell("E21").value = "금액";
    sheet.getCell("E21").font = { size: 10 };
    data.expenses.forEach((e, i) => {
      sheet.getCell(`B${22 + i}`).value = getExpenseTypeText(e.type);
      sheet.getCell(`E${22 + i}`).value = e.amount;
      sheet.getCell(`B${22 + i}`).font = { size: 10 };
    });
    sheet.getCell("B34").value = "소계";
    sheet.getCell("E34").value = {
      formula: `=SUM(E22:E33)`,
      result: data.expenses.reduce((sum, curr) => (sum += curr.amount), 0),
    };

    // 셀 스타일 설정 =========================================================================
    for (let col = 1; col <= 8; col++) {
      const column = sheet.getColumn(col);

      // 열 너비 설정
      if (col === 1 || col === 6)
        column.width = 2.71; // A, F
      else if (col === 4 || col === 7) {
        column.width = 15; // D, G
        if (col === 7) column.font = { size: 11, bold: true }; // G
      } else if (col === 8) {
        column.width = 20; // H
        column.font = { size: 11, bold: true }; // H
      } else if (col === 2)
        column.width = 25; // B
      else column.width = 20;

      // 열 전체  정렬
      column.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        cell.font = {
          ...cell.font,
          name: "맑은 고딕",
        };

        // 숫자 포맷
        if (typeof cell.value === "number" || typeof cell.value === "object") {
          cell.numFmt = "₩ #,##0";
          // 우측 정렬
          cell.alignment = {
            vertical: "middle",
            horizontal: "right",
          };
        }
      });
    }

    // 셀 높이 설정
    for (let col = 1; col <= 44; col++) {
      if ((col >= 1 && col <= 10) || (col >= 12 && col <= 17))
        sheet.getRow(col).height = 26;
      else if (col === 11) sheet.getRow(col).height = 22;
      else sheet.getRow(col).height = 21;
    }

    // 퍼센트 포맷 적용
    const cell = sheet.getCell("C7");
    cell.numFmt = "0.00%";

    // 좌측 정렬
    const leftCells = ["B3", "B4", "B10", "B20"];
    leftCells.forEach((c) => {
      const cell = sheet.getCell(c);
      cell.alignment = { vertical: "middle", horizontal: "left" };
    });

    // 폰트 사이즈 15px
    const font15Cells = ["G3", "G8", "G12"];
    font15Cells.forEach((c) => {
      const cell = sheet.getCell(c);
      cell.font = { ...cell.font, size: 15, bold: true };
    });

    // 상단, 하단 테두리
    const borderCells = ["B11", "C11", "E11", "B21", "C21", "E21"];
    borderCells.forEach((c) => {
      const cell = sheet.getCell(c);
      cell.font = { ...cell.font, size: 10 };
      cell.border = {
        top: { style: "medium", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
      };
    });

    // 하단 도트 테두리
    applyBottomDottedBorder(sheet, 12, 17);
    applyBottomDottedBorder(sheet, 22, 33);

    // 소계 셀
    const resultCells = [
      "B18",
      "C18",
      "D18",
      "E18",
      "B34",
      "C34",
      "D34",
      "E34",
    ];
    resultCells.forEach((c) => {
      const cell = sheet.getCell(c);
      cell.font = { ...cell.font, size: 10, bold: true };
      cell.border = {
        bottom: { style: "thin", color: { argb: "000000" } },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "f0f0f0" },
      };
    });

    // 제목 스타일 지정
    sheet.getCell("B1").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "daf2d0" },
    };
    sheet.getCell("B1").font = {
      name: "맑은 고딕",
      color: { argb: "000000" },
      size: 18,
      bold: true,
    };
  };

  ///////////////////////////////////////
  //     매출 & 카드사 통계 시트 생성     //
  //////////////////////////////////////
  const createRevenueAndCardSheet = async (
    workbook: Workbook,
    data: SettlementMonthExcelResponse,
    year: number,
    month: number,
  ) => {
    const sheet = workbook.addWorksheet(`${getPubName()}  매출&카드사입금`);

    // 해당 월의 마지막 날
    const lastDay = new Date(year, month, 0).getDate();

    // 제목
    sheet.mergeCells("B3 : H3");
    sheet.getCell("B3").value = `날짜별 매출`;

    // 해더
    sheet.getCell("B4").value = "날짜";
    sheet.getCell("C4").value = "현금";
    sheet.getCell("D4").value = "계좌이체";
    sheet.getCell("E4").value = "카드";
    sheet.getCell("F4").value = "래빗";
    sheet.getCell("G4").value = "현금성매출";
    sheet.getCell("H4").value = "래빗포함매출";

    // 날짜별 매출
    data.revenues.forEach((revenue, i) => {
      const date = new Date(revenue.date);
      sheet.getCell(`B${i + 5}`).value = `${date.getDate()}일`;
      // 일요일 = 빨간색
      if (date.getDay() === 7)
        sheet.getCell(`B${i + 5}`).font = { color: { argb: "ff0000" } };

      sheet.getCell(`C${i + 5}`).value = revenue.totalCash;
      sheet.getCell(`D${i + 5}`).value = revenue.totalBank;
      sheet.getCell(`E${i + 5}`).value = revenue.totalCard;
      sheet.getCell(`F${i + 5}`).value = revenue.totalSeed;
      sheet.getCell(`G${i + 5}`).value = {
        formula: `=SUM(C${i + 5}:E${i + 5})`,
        result: revenue.cashRevenue,
      };
      sheet.getCell(`H${i + 5}`).value = {
        formula: `=SUM(C${i + 5}:F${i + 5})`,
        result: revenue.cashRevenue + revenue.totalSeed,
      };
    });

    // 합계
    sheet.getCell(`B${lastDay + 5}`).value = "계";
    sheet.getCell(`C${lastDay + 5}`).value = {
      formula: `=SUM(C5:C${lastDay + 4})`,
      result: data.revenues.reduce((sum, curr) => (sum += curr.totalCash), 0),
    };
    sheet.getCell(`D${lastDay + 5}`).value = {
      formula: `=SUM(D5:D${lastDay + 4})`,
      result: data.revenues.reduce((sum, curr) => (sum += curr.totalBank), 0),
    };
    sheet.getCell(`E${lastDay + 5}`).value = {
      formula: `=SUM(E5:E${lastDay + 4})`,
      result: data.revenues.reduce((sum, curr) => (sum += curr.totalCard), 0),
    };
    sheet.getCell(`F${lastDay + 5}`).value = {
      formula: `=SUM(F5:F${lastDay + 4})`,
      result: data.revenues.reduce((sum, curr) => (sum += curr.totalSeed), 0),
    };
    sheet.getCell(`G${lastDay + 5}`).value = {
      formula: `=SUM(G5:G${lastDay + 4})`,
      result: data.revenues.reduce((sum, curr) => (sum += curr.cashRevenue), 0),
    };
    sheet.getCell(`H${lastDay + 5}`).value = {
      formula: `=SUM(H5:H${lastDay + 4})`,
      result: data.revenues.reduce(
        (sum, curr) => (sum += curr.cashRevenue + curr.totalSeed),
        0,
      ),
    };

    // 제목
    sheet.mergeCells("K3 : L3");
    sheet.getCell("K3").value = `카드사 입금액`;

    // 해더
    sheet.getCell("K4").value = "날짜";
    sheet.getCell("L4").value = "입금액";

    for (let day = 1; day <= lastDay; day++) {
      sheet.getCell(`K${day + 4}`).value = `${day}일`;

      // 해당 날짜의 카드사 입금액
      const income = data.cardIncomes.find((card) => card.day === day);
      sheet.getCell(`L${day + 4}`).value = income ? income.amount : 0;
    }

    // 합계
    sheet.getCell(`K${lastDay + 5}`).value = "총합";
    sheet.getCell(`L${lastDay + 5}`).value = {
      formula: `=SUM(L5:L${lastDay + 4})`,
      result: data.cardIncomes.reduce((sum, curr) => (sum += curr.amount), 0),
    };

    // 셀 스타일 설정 =========================================================================
    sheet.eachRow({ includeEmpty: false }, (row) => {
      row.height = 17; // 기본 높이 지정

      row.eachCell((cell) => {
        // border 적용
        if (cell.value !== null && cell.value !== undefined) {
          if (row.number === 1) return;

          cell.font = { size: 11 };

          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }
      });
    });

    for (let col = 1; col <= 14; col++) {
      const column = sheet.getColumn(col);

      // 열 너비 설정
      if (col === 1 || col === 9 || col === 10)
        column.width = 10; // A, I, J
      else if (col === 2 || col === 11)
        column.width = 12; // B, k
      else column.width = 20;

      // 열 전체  정렬
      column.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        cell.font = {
          ...cell.font,
          name: "맑은 고딕",
        };

        // 숫자 포맷
        if (typeof cell.value === "number" || typeof cell.value === "object") {
          cell.numFmt = "₩ #,##0";
          // 우측 정렬
          cell.alignment = {
            vertical: "middle",
            horizontal: "right",
          };
        }
      });
    }

    // 제목 셀 높이 설정
    sheet.getRow(3).height = 30;
    sheet.getRow(lastDay + 5).height = 30;

    // 배경 및 폰트 지정
    const grayCells = [];
    for (let col = 5; col <= lastDay + 4; col++) {
      sheet.getCell(`C${col}`).font = { name: "맑은 고딕", size: 12 };
      sheet.getCell(`D${col}`).font = { name: "맑은 고딕", size: 12 };
      sheet.getCell(`E${col}`).font = { name: "맑은 고딕", size: 12 };
      sheet.getCell(`F${col}`).font = { name: "맑은 고딕", size: 12 };
      grayCells.push(`G${col}`);
      grayCells.push(`H${col}`);
    }
    grayCells.forEach((c) => {
      const cell = sheet.getCell(c);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "a7a7a7" },
      };
      cell.font = { ...cell.font, bold: true, size: 12 };
    });

    // 테이블 상단, 하단 셀
    const resultCells = [
      `B4`,
      `C4`,
      `D4`,
      `E4`,
      `F4`,
      `G4`,
      `H4`,
      `B${lastDay + 5}`,
      `C${lastDay + 5}`,
      `D${lastDay + 5}`,
      `E${lastDay + 5}`,
      `F${lastDay + 5}`,
      `G${lastDay + 5}`,
      `H${lastDay + 5}`,
    ];
    resultCells.forEach((c) => {
      const cell = sheet.getCell(c);
      cell.font = { ...cell.font, size: 10, bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "bfbfbf" },
      };
    });

    // 제목 스타일 지정
    sheet.getCell("B3").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "0f9ed5" },
    };
    sheet.getCell("B3").font = { name: "맑은 고딕", size: 16, bold: true };
    sheet.getCell("K3").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "0f9ed5" },
    };
    sheet.getCell("K3").font = { name: "맑은 고딕", size: 16, bold: true };
  };

  ///////////////////////////////////
  //    지출 통계 과목1 시트 생성     //
  //////////////////////////////////
  const createExpenseType1 = async (
    workbook: Workbook,
    data: SettlementExpenseTypeAndExpensesDto[],
  ) => {
    const sheet = workbook.addWorksheet(`${getPubName()} 과목 -1`);

    const typeList = [
      { value: ExpenseMainType.TAX_PUBLIC, name: "[ 1 ] 세 금 과 공 과" },
      { value: ExpenseMainType.RABBIT_PURCHASE, name: "[ 2 ] 로 열 티 외" },
      { value: ExpenseMainType.LABOR, name: "[ 3 ] 인 건 비" },
      { value: ExpenseMainType.LABOR, name: "[ 3 ] 인 건 비" },
      {
        value: ExpenseMainType.RENT_MANAGEMENT,
        name: "[ 4 ] 임 차 료 및 관 리 비",
      },
      { value: ExpenseMainType.SALARY, name: "[ 5 ] 급 여" },
      {
        value: ExpenseMainType.DRINK_PURCHASE,
        name: "[ 6 ] 주 류 음 료 매 입",
      },
      {
        value: ExpenseMainType.TAX_LABOR_BOOK,
        name: "[ 7 ] 세 금 노 무 기 장",
      },
    ];

    const COLS_PER_BLOCK = 4;

    typeList.forEach((type, index) => {
      if (index > 7) return;
      const isSecondPage = index >= 4;

      // 시작 row 결정
      const row = isSecondPage ? 31 : 2;
      const end = isSecondPage ? 58 : 29;

      // 컬럼 index (0~3)
      const colIndex = index % 4;

      // 시작 column 번호
      const col = colIndex * COLS_PER_BLOCK + 1;

      const A = numberToExcelColumn(col);
      const B = numberToExcelColumn(col + 1);
      const C = numberToExcelColumn(col + 2);

      // 제목
      sheet.mergeCells(`${A}${row}:${C}${row}`);
      sheet.getCell(A + row).value = type.name;
      sheet.getCell(A + row).font = { size: 16, bold: true };

      // 헤더
      sheet.getCell(A + (row + 1)).value = "날짜";
      sheet.getCell(B + (row + 1)).value = "내역";
      sheet.getCell(C + (row + 1)).value = "금액";

      // 상세 데이터
      const typeData = data.find((t) => t.type === type.value);

      // 합계
      sheet.getCell(A + end).value = "계";
      sheet.getCell(A + end).font = { bold: true, size: 11 };
      sheet.mergeCells(`${A}${end}:${B}${end}`);
      sheet.getCell(C + end).value = {
        formula: `=SUM(${C + (row + 2)}:${C + (end - 1)})`,
        result: typeData ? +typeData?.amount : 0,
      };
      sheet.getCell(C + end).font = { bold: true, size: 11 };

      if (typeData) {
        if (type.value === ExpenseMainType.TAX_PUBLIC) {
          // 세금과 공과 ==========================================================
          // 상세타입 별 리스트 조회
          const subList = typeData.list.filter(
            (t) => t.subType === ExpenseSubType.TAX,
          );
          const subList2 = typeData.list.filter(
            (t) => t.subType === ExpenseSubType.PUBLIC_CHARGE,
          );

          const subStart1 = row + 2;
          subList.forEach((item, i) => {
            sheet.getCell(A + (subStart1 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (subStart1 + i)).value = item.description;
            sheet.getCell(C + (subStart1 + i)).value = item.amount;
          });

          sheet.getCell(B + (subStart1 + subList.length)).value = "세금 계";
          sheet.getCell(B + (subStart1 + subList.length)).font = {
            bold: true,
            size: 11,
          };
          sheet.getCell(C + (subStart1 + subList.length)).value = subList.length
            ? {
                formula: `=SUM(${C + subStart1}:${
                  C + (subStart1 + subList.length - 1)
                })`,
                result: subList.reduce((sum, curr) => (sum += curr.amount), 0),
              }
            : 0;
          sheet.getCell(C + (subStart1 + subList.length)).font = {
            bold: true,
            size: 11,
          };

          const subStart2 = subStart1 + subList.length + 2;
          subList2.forEach((item, i) => {
            sheet.getCell(A + (subStart2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (subStart2 + i)).value = item.description;
            sheet.getCell(C + (subStart2 + i)).value = item.amount;
          });
          sheet.getCell(B + (subStart2 + subList2.length)).value = "공과금 계";
          sheet.getCell(B + (subStart2 + subList2.length)).font = {
            bold: true,
            size: 11,
          };
          sheet.getCell(C + (subStart2 + subList2.length)).value =
            subList2.length
              ? {
                  formula: `=SUM(${C + subStart2}:${
                    C + (subStart2 + subList2.length - 1)
                  })`,
                  result: subList2.reduce(
                    (sum, curr) => (sum += curr.amount),
                    0,
                  ),
                }
              : 0;
          sheet.getCell(C + (subStart2 + subList2.length)).font = {
            bold: true,
            size: 11,
          };

          // 합계
          sheet.getCell(C + end).value = {
            formula: `=SUM(${C + (subStart1 + subList.length)},${
              C + (subStart2 + subList2.length)
            })`,
          };
        } else if (type.value === ExpenseMainType.LABOR) {
          // 인건비 =============================================================
          // list 분해
          const limit = 25;
          const list = typeData.list.slice(
            (index - 2) * limit,
            (index - 1) * limit,
          );
          list.forEach((item, i) => {
            sheet.getCell(A + (row + 2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (row + 2 + i)).value = item.description;
            sheet.getCell(C + (row + 2 + i)).value = item.amount;
          });

          // 합계
          sheet.getCell(A + end).value = "계";
          sheet.getCell(A + end).font = { bold: true, size: 11 };
          sheet.getCell(C + end).value =
            index === 2
              ? {
                  formula: `=SUM(${C + (row + 2)}:${C + (end - 1)})`,
                  result: list.reduce((sum, curr) => (sum += curr.amount), 0),
                }
              : {
                  formula: "=SUM(K4:K28, O4:O28)",
                  result: typeData.amount,
                };
          sheet.getCell(C + end).font = { bold: true, size: 11 };
        } else if (type.value === ExpenseMainType.RENT_MANAGEMENT) {
          // 임차료 및 관리비 =============================================================
          // 상세타입 별 리스트 조회
          const subList = typeData.list.filter(
            (t) => t.subType === ExpenseSubType.RENT,
          );
          const subList2 = typeData.list.filter(
            (t) => t.subType === ExpenseSubType.MANAGEMENT_FEE,
          );

          const subStart1 = row + 2;
          subList.forEach((item, i) => {
            sheet.getCell(A + (subStart1 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (subStart1 + i)).value = item.description;
            sheet.getCell(C + (subStart1 + i)).value = item.amount;
          });

          sheet.getCell(B + (subStart1 + subList.length)).value = "임차료 계";
          sheet.getCell(B + (subStart1 + subList.length)).font = {
            bold: true,
            size: 11,
          };
          sheet.getCell(C + (subStart1 + subList.length)).value = subList.length
            ? {
                formula: `=SUM(${C + subStart1}:${
                  C + (subStart1 + subList.length - 1)
                })`,
                result: subList.reduce((sum, curr) => (sum += curr.amount), 0),
              }
            : 0;
          sheet.getCell(C + (subStart1 + subList.length)).font = {
            bold: true,
            size: 11,
          };

          const subStart2 = subStart1 + subList.length + 2;
          subList2.forEach((item, i) => {
            sheet.getCell(A + (subStart2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (subStart2 + i)).value = item.description;
            sheet.getCell(C + (subStart2 + i)).value = item.amount;
          });
          sheet.getCell(B + (subStart2 + subList2.length)).value = "관리비 계";
          sheet.getCell(B + (subStart2 + subList2.length)).font = {
            bold: true,
            size: 11,
          };
          sheet.getCell(C + (subStart2 + subList2.length)).value =
            subList2.length
              ? {
                  formula: `=SUM(${C + subStart2}:${
                    C + (subStart2 + (subList2.length - 1))
                  })`,
                  result: subList2.reduce(
                    (sum, curr) => (sum += curr.amount),
                    0,
                  ),
                }
              : 0;
          sheet.getCell(C + (subStart2 + subList2.length)).font = {
            bold: true,
            size: 11,
          };

          // 합계
          sheet.getCell(C + end).value = {
            formula: `=SUM(${C + (subStart1 + subList.length)},${
              C + (subStart2 + subList2.length)
            })`,
          };
        } else {
          typeData.list.forEach((item, i) => {
            sheet.getCell(A + (row + 2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (row + 2 + i)).value = item.description;
            sheet.getCell(C + (row + 2 + i)).value = +item.amount;
          });
        }
      }
    });

    // 스타일 적용
    expenseSheetStyle(sheet);
  };

  ///////////////////////////////////
  //    지출 통계 과목2 시트 생성     //
  //////////////////////////////////
  const createExpenseType2 = async (
    workbook: Workbook,
    data: SettlementExpenseTypeAndExpensesDto[],
  ) => {
    const sheet = workbook.addWorksheet(`${getPubName()} 과목 -2`);

    const typeList = [
      { value: ExpenseMainType.MARKETING, name: "[ 8 ] 광 고 마 케 팅 비" },
      { value: ExpenseMainType.MARKETING, name: "[ 8 ] 광 고 마 케 팅 비" },
      { value: ExpenseMainType.SUPPLIES_RENTAL, name: "[ 10 ] 비 품 및 렌 탈" },
      { value: ExpenseMainType.SUPPLIES_RENTAL, name: "[ 10 ] 비 품 및 렌 탈" },
      {
        value: ExpenseMainType.INSURANCE_COMM,
        name: "[ 9 ] 보 험 료 및 통 신 비",
      },
      { value: ExpenseMainType.ETC, name: "[ 11 ] 기 타" },
      { value: ExpenseMainType.INTERIOR, name: "[ 12 ] 인 테 리 어" },
    ];

    const COLS_PER_BLOCK = 4;

    typeList.forEach((type, index) => {
      if (index > 7) return;
      const isSecondPage = index >= 4;

      // 시작 row 결정
      const row = isSecondPage ? 31 : 2;
      const end = isSecondPage ? 58 : 29;

      // 컬럼 index (0~3)
      const colIndex = index % 4;

      // 시작 column 번호
      const col = colIndex * COLS_PER_BLOCK + 1;

      const A = numberToExcelColumn(col);
      const B = numberToExcelColumn(col + 1);
      const C = numberToExcelColumn(col + 2);

      // 제목
      sheet.mergeCells(`${A}${row}:${C}${row}`);
      sheet.getCell(A + row).value = type.name;
      sheet.getCell(A + row).font = { size: 16, bold: true };

      // 헤더
      sheet.getCell(A + (row + 1)).value = "날짜";
      sheet.getCell(B + (row + 1)).value = "내역";
      sheet.getCell(C + (row + 1)).value = "금액";

      // 상세 데이터
      const typeData = data.find((t) => t.type === type.value);

      // 합계
      sheet.getCell(A + end).value = "계";
      sheet.getCell(A + end).font = { bold: true, size: 11 };
      sheet.mergeCells(`${A}${end}:${B}${end}`);
      sheet.getCell(C + end).value = {
        formula: `=SUM(${C + (row + 2)}:${C + (end - 1)})`,
        result: typeData ? typeData?.amount : 0,
      };
      sheet.getCell(C + end).font = { bold: true, size: 11 };

      if (typeData) {
        if (type.value === ExpenseMainType.SUPPLIES_RENTAL) {
          // 비품 및 렌탈 =============================================================
          // list 분해
          const limit = 25;
          const list = typeData.list.slice(
            (index - 2) * limit,
            (index - 1) * limit,
          );
          list.forEach((item, i) => {
            sheet.getCell(A + (row + 2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (row + 2 + i)).value = item.description;
            sheet.getCell(C + (row + 2 + i)).value = item.amount;
          });

          // 합계
          sheet.getCell(A + end).value = "계";
          sheet.getCell(A + end).font = { bold: true, size: 11 };
          sheet.getCell(C + end).value =
            index === 2
              ? {
                  formula: `=SUM(${C + (row + 2)}:${C + (end - 1)})`,
                  result: list.reduce((sum, curr) => (sum += curr.amount), 0),
                }
              : {
                  formula: "=SUM(K4:K28, O4:O28)",
                  result: typeData.amount,
                };
          sheet.getCell(C + end).font = { bold: true, size: 11 };
        } else if (type.value === ExpenseMainType.MARKETING) {
          // 광고 마케팅비 =============================================================
          // list 분해
          const limit = 25;
          const list = typeData.list.slice(index * limit, (index + 1) * limit);
          list.forEach((item, i) => {
            sheet.getCell(A + (row + 2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (row + 2 + i)).value = item.description;
            sheet.getCell(C + (row + 2 + i)).value = item.amount;
          });

          // 합계
          sheet.getCell(A + end).value = "계";
          sheet.getCell(A + end).font = { bold: true, size: 11 };
          sheet.getCell(C + end).value =
            index === 0
              ? {
                  formula: `=SUM(${C + (row + 2)}:${C + (end - 1)})`,
                  result: list.reduce((sum, curr) => (sum += curr.amount), 0),
                }
              : {
                  formula: "=SUM(C4:C28, G4:G28)",
                  result: typeData.amount,
                };
          sheet.getCell(C + end).font = { bold: true, size: 11 };
        } else if (type.value === ExpenseMainType.INSURANCE_COMM) {
          // 보험료 및 통신비 =============================================================
          // 상세타입 별 리스트 조회
          const subList = typeData.list.filter(
            (t) => t.subType === ExpenseSubType.INSURANCE,
          );
          const subList2 = typeData.list.filter(
            (t) => t.subType === ExpenseSubType.COMMUNICATION,
          );

          const subStart1 = row + 2;
          subList.forEach((item, i) => {
            sheet.getCell(A + (subStart1 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (subStart1 + i)).value = item.description;
            sheet.getCell(C + (subStart1 + i)).value = item.amount;
          });

          sheet.getCell(B + (subStart1 + subList.length)).value = "보험료 계";
          sheet.getCell(B + (subStart1 + subList.length)).font = {
            bold: true,
            size: 11,
          };
          sheet.getCell(C + (subStart1 + subList.length)).value = subList.length
            ? {
                formula: `=SUM(${C + subStart1}:${
                  C + (subStart1 + subList.length - 1)
                })`,
                result: subList.reduce((sum, curr) => (sum += curr.amount), 0),
              }
            : 0;
          sheet.getCell(C + (subStart1 + subList.length)).font = {
            bold: true,
            size: 11,
          };

          const subStart2 = subStart1 + subList.length + 2;
          subList2.forEach((item, i) => {
            sheet.getCell(A + (subStart2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (subStart2 + i)).value = item.description;
            sheet.getCell(C + (subStart2 + i)).value = item.amount;
          });
          sheet.getCell(B + (subStart2 + subList2.length)).value = "통신비 계";
          sheet.getCell(B + (subStart2 + subList2.length)).font = {
            bold: true,
            size: 11,
          };
          sheet.getCell(C + (subStart2 + subList2.length)).value =
            subList2.length
              ? {
                  formula: `=SUM(${C + subStart2}:${
                    C + (subStart2 + subList2.length - 1)
                  })`,
                  result: subList2.reduce(
                    (sum, curr) => (sum += curr.amount),
                    0,
                  ),
                }
              : 0;
          sheet.getCell(C + (subStart2 + subList2.length)).font = {
            bold: true,
            size: 11,
          };

          // 합계
          sheet.getCell(C + end).value = {
            formula: `=SUM(${C + (subStart1 + subList.length)},${
              C + (subStart2 + subList2.length)
            })`,
          };
        } else {
          typeData.list.forEach((item, i) => {
            sheet.getCell(A + (row + 2 + i)).value = formatDateKor(
              item.settledAt ?? "",
            );
            sheet.getCell(B + (row + 2 + i)).value = item.description;
            sheet.getCell(C + (row + 2 + i)).value = item.amount;
          });
        }
      }
    });

    // 합계 ==============================================================
    // 제목
    sheet.mergeCells(`M31 : O31`);
    sheet.getCell("M31").value = "[ 13 ] 지 출 총 계";
    sheet.getCell("M31").font = { size: 16, bold: true };

    // 헤더
    sheet.getCell("M32").value = "날짜";
    sheet.getCell("N32").value = "내역";
    sheet.getCell("O32").value = "금액";

    // 1과목 총계
    sheet.getCell("M33").value = "지출";
    sheet.getCell("N33").value = "과목 1페이지 총계";

    const sheetName = `${getPubName()} 과목 -1`;
    sheet.getCell("O33").value = {
      formula: `=SUM('${sheetName}'!C29,'${sheetName}'!G29,'${sheetName}'!O29,'${sheetName}'!C58,'${sheetName}'!G58,'${sheetName}'!K58,'${sheetName}'!O58)`,
    };

    // 2과목 총계
    sheet.getCell("M34").value = "지출";
    sheet.getCell("N34").value = "과목 2페이지 총계";

    sheet.getCell("O34").value = {
      formula: `=SUM(C29,G29,O29,C58,G58,K58)`,
    };

    // 총계
    sheet.getCell("M58").value = "계";
    sheet.getCell("M58").font = { bold: true, size: 11 };
    sheet.mergeCells(`M58 : N58`);
    sheet.getCell("O58").value = {
      formula: `=SUM(O33,O34)`,
      result: data.reduce((sum, curr) => sum + Number(curr.amount || 0), 0),
    };
    sheet.getCell("O58").font = { bold: true, size: 11 };

    // 스타일 적용
    expenseSheetStyle(sheet);
  };

  /////////////////////////////////////
  //        월별 매출 현환 생성        //
  ////////////////////////////////////
  const createNetIncomeSheet = async (
    workbook: Workbook,
    data: SettlementMonthNetIncomeResponse,
    year: number,
  ) => {
    const sheet = workbook.addWorksheet(
      `${getPubName()} 월별 현황 ${year.toString().slice(2)}년`,
    );
    sheet.views = [{ showGridLines: false }]; // 기본 선 제거

    // 제목
    sheet.mergeCells("B1 : P1");
    sheet.getCell("B1").value = `${pub?.pubName} 당기 순이익 월별 현황`;

    // 테이블 해더 생성 ===============================================================================
    sheet.getCell("B3").value = "year";
    sheet.getCell("B4").value = `${year}년`;
    sheet.getCell("C3").value = `당기순이익`;
    sheet.mergeCells("C3 : C4");
    sheet.getCell("B5").value = "수입";
    sheet.getCell("B6").value = "월별매출";
    sheet.mergeCells("B6 : C6");
    sheet.mergeCells("B7 : C7");
    sheet.mergeCells("B8 : C8");
    sheet.getCell("B9").value = "지출";
    sheet.getCell("B10").value = "월별지출";
    sheet.mergeCells("B10 : C10");

    // 데이터 넣기 ===============================================================================
    sheet.getCell("D3").value = "Total";
    sheet.getCell("D4").value = {
      formula: `=SUM(E4:P4)`,
      result: data.totalNetIncome,
    };
    sheet.getCell("D5").value = {
      formula: `=SUM(E5:P5)`,
      result: data.totalRevenue,
    };
    sheet.getCell("D9").value = {
      formula: `=SUM(E9:P9)`,
      result: data.totalExpense,
    };

    data.monthList.forEach((month, index) => {
      const colNum = index + 5;
      const col = numberToExcelColumn(colNum);

      // 순이익
      sheet.getCell(col + 3).value = `${month.month}월`;
      sheet.getCell(col + 4).value = {
        formula: `=ROUNDDOWN(${col}5-${col}9,-4)`,
        result: month.totalNetIncome,
      };

      // 수입
      sheet.getCell(col + 5).value = {
        formula: `=SUM(${col}6:${col}7)`,
        result: month.totalRevenue,
      };
      sheet.getCell(col + 6).value = month.totalRevenue;

      // 지출
      sheet.getCell(col + 9).value = {
        formula: `=SUM(${col}10:${col}11)`,
        result: month.totalExpense,
      };
      sheet.getCell(col + 10).value = month.totalExpense;
    });

    // 셀 스타일 설정 =========================================================================
    for (let col = 1; col <= 16; col++) {
      const column = sheet.getColumn(col);

      // 열 너비 설정
      if (col === 1)
        column.width = 2.71; // A
      else if (col === 2)
        column.width = 11.71; // B
      else column.width = 15;

      // 열 전체 정렬
      column.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        cell.font = {
          ...cell.font,
          size: 10,
          name: "맑은 고딕",
        };

        cell.border = {
          top: { style: "thin", color: { argb: "e8e8e8" } },
          bottom: { style: "thin", color: { argb: "e8e8e8" } },
          right: { style: "thin", color: { argb: "e8e8e8" } },
          left: { style: "thin", color: { argb: "e8e8e8" } },
        };

        // 숫자 포맷
        if (typeof cell.value === "number" || typeof cell.value === "object") {
          cell.numFmt = "₩ #,##0";
          // 우측 정렬
          cell.alignment = {
            vertical: "middle",
            horizontal: "right",
          };
        }
      });
    }

    // 단위
    sheet.getCell("P2").value = "단위: 원";
    sheet.getCell("P2").alignment = {
      vertical: "middle",
      horizontal: "right",
    };

    // 셀 높이 설정
    for (let col = 1; col <= 11; col++) {
      if (col === 2) {
        sheet.getRow(col).height = 21;
      } else if (col === 1) {
        sheet.getRow(col).height = 33;
      } else sheet.getRow(col).height = 33;
    }

    const borderNone = [1, 2, "B3", "B4", "C3"];
    borderNone.forEach((v) => {
      if (typeof v === "number") {
        sheet.getRow(v).border = {
          top: { style: "thin", color: { argb: "ffffff" } },
          bottom: { style: "thin", color: { argb: "ffffff" } },
          right: { style: "thin", color: { argb: "ffffff" } },
          left: { style: "thin", color: { argb: "ffffff" } },
        };
      } else {
        sheet.getCell(v).border = {
          top: { style: "thin", color: { argb: "ffffff" } },
          bottom: { style: "thin", color: { argb: "ffffff" } },
          right: { style: "thin", color: { argb: "ffffff" } },
          left: { style: "thin", color: { argb: "ffffff" } },
        };
      }
    });
    const borderRightNone = ["B5", "B9"];
    borderRightNone.forEach((v) => {
      sheet.getCell(v).border = {
        ...sheet.getCell(v).border,
        right: { style: "thin", color: { argb: "f2f2f2" } },
      };
    });

    // 폰트 사이즈 및 볼드 설정
    sheet.getCell("B1").font = { name: "맑은 고딕", size: 18, bold: true };
    sheet.getCell("B1").alignment = { vertical: "middle", horizontal: "left" };
    sheet.getCell("B3").font = { name: "맑은 고딕", size: 10, bold: true };
    sheet.getCell("B4").font = { name: "맑은 고딕", size: 11, bold: true };
    sheet.getCell("C3").font = { name: "맑은 고딕", size: 12, bold: true };
    sheet.getCell("P2").font = { name: "맑은 고딕", size: 9 };

    for (let index = 2; index <= 16; index++) {
      const colNum = index;
      const col = numberToExcelColumn(colNum);

      // 순이익
      sheet.getCell(col + 3).border = {
        ...sheet.getCell(col + 3).border,
        top: { style: "medium", color: { argb: "000000" } },
      };
      sheet.getCell(col + 4).border = {
        ...sheet.getCell(col + 4).border,
        bottom: { style: "medium", color: { argb: "000000" } },
      };
      if (col !== "B" && col !== "C") {
        sheet.getCell(col + 3).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "daf2d0" },
        };

        sheet.getCell(col + 3).font = {
          ...sheet.getCell(col + 4).font,
          size: 10,
          bold: true,
        };

        sheet.getCell(col + 4).font = {
          ...sheet.getCell(col + 4).font,
          size: 10,
          bold: true,
          color: { argb: "ff0000" },
        };
      }

      // 수입
      sheet.getCell(col + 5).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "f2f2f2" },
      };
      sheet.getCell(col + 5).font = {
        ...sheet.getCell(col + 5).font,
        bold: true,
      };

      // 지출
      sheet.getCell(col + 9).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "f2f2f2" },
      };
      sheet.getCell(col + 9).font = {
        ...sheet.getCell(col + 9).font,
        bold: true,
      };
    }
  };

  // 최종 엑셀 생성
  const createMonthFile = async (year: number, month: number) => {
    if (!pub) return;
    const data = await getSettlementMonthExcel(pub.id, year, month);
    if (!data) return;
    const workbook = new ExcelJS.Workbook();
    workbook.calcProperties = { fullCalcOnLoad: true };
    // 각 시트 탭 생성
    await createClosingSheet(workbook, data.closing, month);
    await createRevenueAndCardSheet(workbook, data, year, month);
    await createExpenseType1(workbook, data.types);
    await createExpenseType2(workbook, data.types);
    await createNetIncomeSheet(workbook, data.netIncome, year);

    excelDownload(
      workbook,
      `${month}월 ${getPubName()} 결산서(${year.toString().slice(2)}년)`,
    );
  };

  const getPubName = () => {
    if (!pub?.pubName) return "";
    if (pub.pubName.includes("러너펍")) {
      return pub.pubName.replace("러너펍", "").trim();
    } else if (pub.pubName.includes("러너라운지")) {
      const name = pub.pubName
        .replace("러너라운지", "")
        .trim()
        .replace("점", "");
      return `${name}라운지`;
    }
    return pub.pubName;
  };

  const excelDownload = async (workbook: Workbook, fileName: string) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName + ".xlsx";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  return {
    createMonthFile,
  };
};
